import { ref, computed, watch, readonly } from 'vue';
import { isNumeric, StoreResources, BillingResources } from 'ihunter-common';

import { useApi } from './useApi';
import { usePurchases } from './usePurchases';
import { useUserStorage } from './useWebStorage';
import { useCatalog } from './useCatalog';
import { useProduct } from './useProduct';
import { useAuthentication } from './useAuthentication';
import { useFirebaseDatabase } from './useFirebaseDatabase';
import { useAnalytics } from './useAnalytics';

const CART_PROMO = 'cart';

const cart = ref({});
const unwatch = ref(null);
const watchedCartUid = ref(null);
const initialized = ref(false);

const totalQuantity = computed(() => {
    return cart.value ? Object.values(cart.value).reduce((total, quantity) => total + quantity, 0) : 0;
});

const itemCount = computed(() => {
    return cart.value ? Object.values(cart.value).length : 0;
});

// Provides cart management and checkout functionality
export function useCart() {
    const { user } = useAuthentication();
    const { watchData, setData, removeData, unwatchPath } = useFirebaseDatabase();
    const promo = useUserStorage(CART_PROMO, '');

    const { products } = useCatalog();
    const { product } = useProduct();
    const { purchasedProducts } = usePurchases();
    const { httpPostJson } = useApi('api');
    const { lookupAnalyticsIds, logAddToCart, logRemoveFromCart, logBeginCheckout } = useAnalytics();

    if (!initialized.value) {
        // Watch for user changes and cleanup cart
        watch(
            user,
            (newUser, oldUser) => {
                if (oldUser && (!newUser || newUser.uid !== oldUser.uid)) {
                    // User logged out or changed
                    unwatchCart();
                } else if (newUser && (!oldUser || newUser.uid !== oldUser.uid)) {
                    // User logged in or changed to a new user
                    watchCart();
                }
            },
            { immediate: true },
        );

        watch(
            purchasedProducts,
            () => {
                console.log('Purchases updated, revalidating cart items');
                // Revalidate cart items against purchases to remove newly owned products
                for (let sku of Object.keys(purchasedProducts.value)) {
                    if (cart.value && cart.value[sku]) {
                        console.log(`Cart item already purchased; removing '${sku}' from cart`);
                        delete cart.value[sku];
                        removeCartItemData(sku);
                    }
                }
            },
            { immediate: true },
        );

        initialized.value = true;
    }

    async function watchCart() {
        if (!user.value) {
            throw new Error('Cannot watch cart for an unauthenticated user');
        }

        const uid = user.value.uid;
        const path = StoreResources.Cart(uid);

        // Prevent multiple watchers
        if (unwatch.value) {
            if (watchedCartUid.value === uid) {
                console.warn('Cart is already being watched');
                return;
            }

            // Defensive cleanup if watcher exists for a different user
            await unwatchCart();
        }

        watchedCartUid.value = uid;

        try {
            // Watch for remote changes. These only update local state; writing the cart back here would echo a
            // stale or catalog-filtered snapshot over changes made by other clients (e.g. the Store in another tab)
            await watchData(path, async (data) => {
                cart.value = verifyCartData(data);
                console.log(`Cart updated`, data);
            });

            unwatch.value = () => unwatchPath(path);

            console.log(`Watching cart`);
        } catch (error) {
            console.error('Failed to watch cart:', error);
            throw error;
        }
    }

    async function unwatchCart() {
        if (unwatch.value) {
            unwatch.value();
            unwatch.value = null;
            console.log(`Stopped watching cart for ${watchedCartUid.value}`);
        }

        watchedCartUid.value = null;
        cart.value = {};
    }

    // Cart items are written individually so concurrent changes from other clients aren't overwritten
    function cartItemPath(sku) {
        return watchedCartUid.value ? `${StoreResources.Cart(watchedCartUid.value)}/${sku}` : null;
    }

    function setCartItemData(sku, quantity) {
        const path = cartItemPath(sku);
        if (path) {
            setData(path, quantity).catch((error) => console.error(`Failed to save cart item '${sku}'`, error));
        }
    }

    function removeCartItemData(sku) {
        const path = cartItemPath(sku);
        if (path) {
            removeData(path).catch((error) => console.error(`Failed to remove cart item '${sku}'`, error));
        }
    }

    function verifyCartData(data) {
        const verifiedCart = {};
        if (!data) {
            return verifiedCart;
        }

        for (const [sku, quantity] of Object.entries(data)) {
            if (typeof sku === 'string' && isNumeric(quantity) && quantity > 0) {
                // If products catalog exists, verify SKU is valid
                if (products.value) {
                    if (products.value[sku]) {
                        verifiedCart[sku] = quantity;
                    } else {
                        console.warn(`Ignoring unknown cart SKU: ${sku} (not found in product catalog)`);
                    }
                } else {
                    // Products not loaded yet, keep the item
                    verifiedCart[sku] = quantity;
                }
            } else {
                console.warn(`Invalid cart item skipped: ${sku} with quantity ${quantity}`);
            }
        }
        return verifiedCart;
    }

    async function checkout() {
        try {
            const { analyticsClientId, analyticsSessionId } = await lookupAnalyticsIds();
            let origin = product.value?.name || null;

            console.log(`Cart checkout from '${origin}' with promo code: '${promo.value}'`, items.value);

            //console.log("checkout");
            let response = await httpPostJson(
                BillingResources.Checkout(),
                {
                    items: items.value,
                    promo: promo.value,
                    origin: origin,
                    // affiliate: config.affiliate,
                    success: window.location.href,
                    cancel: window.location.href,
                    analyticsClientId,
                    analyticsSessionId,
                },
                { user },
            );

            logBeginCheckout(Object.keys(cart.value), promo.value);

            console.log(response);
            window.location.replace(response.url);
        } catch (error) {
            console.error(`Checkout error (${error.message})`, error);
        }
    }

    function buy(sku) {
        console.log(`Cart buy item: '${sku}'`);
        if (!cart.value[sku]) {
            update(sku, 1);
            logAddToCart(sku);
        }
    }

    function update(sku, quantity) {
        if (!sku || sku === undefined) {
            console.warn(`Unable to update cart; invalid product '${sku}': ${quantity}`);
            return;
        }
        if (!isNumeric(quantity) || quantity < 0) {
            console.warn(`Unable to update cart; invalid quantity for '${sku}': ${quantity}`);
            return;
        }
        if (quantity === 0) {
            remove(sku);
            return;
        }
        console.log(`Cart update '${sku}': ${quantity}`);
        cart.value[sku] = quantity;
        setCartItemData(sku, quantity);
    }

    function remove(sku) {
        delete cart.value[sku];
        removeCartItemData(sku);
        logRemoveFromCart(sku);
    }

    const items = computed(() => {
        return Object.keys(cart.value).map((sku) => {
            return {
                sku: sku,
                count: cart.value[sku],
            };
        });
    });

    return {
        // reactive state
        cart: readonly(cart),
        itemCount,
        totalQuantity,

        promo,

        checkout,

        addCartItem: buy,
        updateCartItem: update,
        removeCartItem: remove,
    };
}
